import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';

import { TipoLancamento } from '@models/tipo-lancamento';
import { CadastroLoteComponent } from '../../../pages/financeiro/cadastro-lote-lancamentos/components/cadastro-lote/cadastro-lote.component';
import { TipoLancamentoApiService } from '@services/tipo-lancamento-api.service';
import { Permissao } from '@models/enums/permissao';
import { DialogConfirmacaoComponent } from '@components/dialog-confirmacao/dialog-confirmacao.component';
import { ConfiguracaoCaixaApiService } from '../../../pages/financeiro/configuracao-caixa/services/configuracao-caixa-api.service';

export interface CadastroLoteLancamentosDialogData {
  data: Date | null;
  nomeLote?: string;
  valorLancamentoBanco?: number;
  contaCaixaId?: number;
}

@Component({
  selector: 'app-cadastro-lote-lancamentos-dialog',
  standalone: false,
  templateUrl: './cadastro-lote-lancamentos-dialog.component.html',
  styleUrl: './cadastro-lote-lancamentos-dialog.component.scss'
})
export class CadastroLoteLancamentosDialogComponent implements OnInit, AfterViewInit {

  protected get permissaoEditarLancamento() { return Permissao.EDITAR_LANCAMENTO_CAIXA; }

  title: string = 'Lançamento em lote';

  tiposLancamento: TipoLancamento[] = [];

  @ViewChild(CadastroLoteComponent)
  cadastroLote: CadastroLoteComponent;

  private tiposLancamentoCarregados = false;
  private viewPronta = false;

  // Só é preenchido quando o dialog é aberto a partir de "Adicionar lote correspondente" — usado
  // para validar, antes de salvar, que o lançamento correspondente é de um tipo da conta em conciliação.
  private tipoLancamentoIdsDaContaEmConciliacao: Set<number> | null = null;

  constructor(
    private tipoLancamentoApiService: TipoLancamentoApiService,
    private configuracaoCaixaApiService: ConfiguracaoCaixaApiService,
    private dialogRef: MatDialogRef<CadastroLoteLancamentosDialogComponent>,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) private dadosIniciais: CadastroLoteLancamentosDialogData | null,
  ) {
    if (this.dadosIniciais?.valorLancamentoBanco != null && this.dadosIniciais?.contaCaixaId != null) {
      this.configuracaoCaixaApiService.getTiposDaConta(this.dadosIniciais.contaCaixaId).subscribe((tipos) => {
        this.tipoLancamentoIdsDaContaEmConciliacao = new Set(tipos.map(t => t.id));
      });
    }
  }

  ngOnInit(): void {
    this.tipoLancamentoApiService.getTiposLancamento().subscribe((tipos: TipoLancamento[]) => {
      this.tiposLancamento = tipos;
      this.tiposLancamentoCarregados = true;
      this.buscarLoteInicialSeProntoPara();
    });
  }

  ngAfterViewInit(): void {
    this.viewPronta = true;

    if (this.dadosIniciais?.data && !this.dadosIniciais?.nomeLote) {
      this.cadastroLote.setDataInicial(this.dadosIniciais.data);
      if (this.dadosIniciais.valorLancamentoBanco != null)
        this.cadastroLote.preencherValorCorrespondente(this.dadosIniciais.valorLancamentoBanco);
      return;
    }

    this.buscarLoteInicialSeProntoPara();
  }

  // createRow() resolve o tipoLancamento de cada linha usando tiposLancamento no momento da criação,
  // então buscar() só pode rodar depois que os tipos chegarem, senão as linhas ficam sem tipo selecionado.
  private buscarLoteInicialSeProntoPara(): void {
    if (!this.viewPronta || !this.tiposLancamentoCarregados) return;
    if (!this.dadosIniciais?.nomeLote || !this.dadosIniciais?.data) return;

    this.cadastroLote.buscar(this.dadosIniciais.nomeLote, this.dadosIniciais.data.toISOString());
  }

  public onLoteEncontrado(): void {
    this.title = 'Editar lote de lançamentos';
  }

  public salvarClick(): void {
    const motivoAviso = this.obterMotivoAvisoLoteCorrespondente();
    if (motivoAviso) {
      this.confirmarSalvarComAvisoLoteCorrespondente(motivoAviso);
      return;
    }

    this.salvarLote();
  }

  // Caso de exceção: dialog aberto a partir de "Adicionar lote correspondente" (ver
  // lancamentos-table.component.ts), onde valorLancamentoBanco é preenchido com o valor do
  // lançamento do banco selecionado. Verifica, antes de salvar, se o lado oposto do lote (Ex:
  // banco -5000 -> créditos) ainda contém uma linha com esse valor e de um tipo pertencente à
  // conta em conciliação — e distingue qual das duas condições falhou, para a mensagem de aviso.
  private obterMotivoAvisoLoteCorrespondente(): 'valor' | 'tipo' | null {
    const valorLancamentoBanco = this.dadosIniciais?.valorLancamentoBanco;
    if (valorLancamentoBanco == null || this.tipoLancamentoIdsDaContaEmConciliacao == null) return null;

    const ehCreditoOposto = valorLancamentoBanco < 0;
    const valorAbsoluto = Math.abs(valorLancamentoBanco);

    if (this.cadastroLote.possuiValorDeTipoDaConta(ehCreditoOposto, valorAbsoluto, this.tipoLancamentoIdsDaContaEmConciliacao))
      return null;

    return this.cadastroLote.possuiValor(ehCreditoOposto, valorAbsoluto) ? 'tipo' : 'valor';
  }

  private confirmarSalvarComAvisoLoteCorrespondente(motivo: 'valor' | 'tipo'): void {
    const ehCreditoOposto = this.dadosIniciais!.valorLancamentoBanco! < 0;
    const ladoLabel = ehCreditoOposto ? 'créditos' : 'débitos';

    const mensagem = motivo === 'tipo'
      ? `Valor selecionado encontrado nos ${ladoLabel}, mas em um tipo que não pertence a essa conta, a conciliação do lançamento selecionado pode não ser possível`
      : `Valor selecionado não encontrado nos ${ladoLabel}, a conciliação do lançamento selecionado pode não ser possível`;

    const dialogRef = this.dialog.open(DialogConfirmacaoComponent, {
      width: '467px',
      panelClass: 'no-padding-dialog',
      autoFocus: false
    });
    dialogRef.componentInstance.titulo = "Adicionar lote correspondente";
    dialogRef.componentInstance.mensagem = mensagem;
    dialogRef.componentInstance.mensagemBold = 'Deseja continuar?';
    dialogRef.componentInstance.acaoConfirmacao = this.salvarLote.bind(this);
  }

  private salvarLote(): void {
    this.cadastroLote.salvar(() => this.dialogRef.close(true));
  }
}
